import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import ProjectCard from '../components/ProjectCard';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import StatCounter from '../components/StatCounter';
import CTASection from '../components/CTASection';
import { getFeaturedProjects } from '../data/projects';
import { services } from '../data/services';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const testimonials = [
  {
    quote: 'The app they built for us exceeded all expectations. Clean code, beautiful design, and launched on time. Our users absolutely love it.',
    author: 'Alex Rivera',
    role: 'Founder, FinTech Startup',
    gradient: 'linear-gradient(135deg, #539df5 0%, #2858a8 100%)',
    initials: 'AR',
  },
  {
    quote: 'From logo design to full-stack development, they handled everything with incredible professionalism. Our new platform runs flawlessly.',
    author: 'Maria Chen',
    role: 'CEO, E-commerce Brand',
    gradient: 'linear-gradient(135deg, #f3727f 0%, #a83a44 100%)',
    initials: 'MC',
  },
  {
    quote: 'They don\'t just write code — they craft experiences. Our desktop app went from concept to cross-platform launch in record time.',
    author: 'James Park',
    role: 'Product Manager, SaaS Company',
    gradient: 'linear-gradient(135deg, #a855f7 0%, #6b21a8 100%)',
    initials: 'JP',
  },
];

function FloatingOrb({
  color,
  size,
  top,
  left,
  delay,
}: {
  color: string;
  size: number;
  top: string;
  left: string;
  delay: number;
}) {
  return (
    <div
      className="hero-orb"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        background: color,
        top,
        left,
        animationDelay: `${delay}s`,
      }}
      aria-hidden="true"
    />
  );
}

export default function HomePage() {
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  // Apply SEO
  useSEO(PAGE_SEO.home);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const featuredProjects = getFeaturedProjects();

  return (
    <PageTransition>
      {/* ── Hero Section ── */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-orbs" aria-hidden="true">
          <FloatingOrb
            color="rgba(30, 215, 96, 0.15)"
            size={400}
            top="10%"
            left="15%"
            delay={0}
          />
          <FloatingOrb
            color="rgba(83, 157, 245, 0.12)"
            size={350}
            top="30%"
            left="60%"
            delay={1.5}
          />
          <FloatingOrb
            color="rgba(255, 164, 43, 0.10)"
            size={300}
            top="60%"
            left="30%"
            delay={3}
          />
        </div>
        <div className="hero-noise" aria-hidden="true" />
        <div className="hero-content container">
          <div
            className={`hero-badge badge${loaded ? ' animate-fade-in-up' : ''}`}
            style={{ animationDelay: '0.1s' }}
          >
            <span className="badge__dot badge__dot--pulse" />
            <span className="caption-bold">Available for projects</span>
          </div>
          <h1
            className={`hero-headline h-hero${loaded ? ' animate-fade-in-up' : ''}`}
            style={{ animationDelay: '0.25s' }}
          >
            We build{' '}
            <span className="hero-headline__accent">digital products</span>{' '}
            that people love to use.
          </h1>
          <p
            className={`hero-subtitle body-lg${loaded ? ' animate-fade-in-up' : ''}`}
            style={{ animationDelay: '0.4s' }}
          >
            We are a software development studio specializing in mobile apps, desktop
            applications, web platforms, and brand identity. We turn ideas into polished,
            performant products that users love — from concept to launch and beyond.
          </p>
          <div
            className={`hero-actions${loaded ? ' animate-fade-in-up' : ''}`}
            style={{ animationDelay: '0.55s' }}
          >
            <Link to="/work" className="btn btn--primary btn--large">
              View Our Work
            </Link>
            <Link to="/contact" className="btn btn--outlined btn--large">
              Start a Project
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <ScrollReveal>
        <section className="section stats-bar">
          <div className="container">
            <div className="stats-bar__inner grid-4">
              <StatCounter target={4} suffix="+" label="Published Apps" />
              <StatCounter target={50} suffix="+" label="Projects Completed" />
              <StatCounter target={20} suffix="+" label="Logo Designs" />
              <StatCounter target={100} suffix="%" label="Client Satisfaction" />
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── Featured Work ── */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <h2 className="section-header__title h-2">Featured Work</h2>
                <p className="section-header__subtitle">
                  A selection of our best apps, websites, and brand identities.
                </p>
              </div>
              <Link to="/work" className="btn btn--ghost btn--small">
                View All
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            </div>
            <div className="grid-2">
              {featuredProjects.slice(0, 4).map((project, i) => (
                <ProjectCard key={project.id} project={project} index={i} />
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── Services Preview ── */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <h2 className="section-header__title h-2">What We Build</h2>
                <p className="section-header__subtitle">
                  From mobile apps to complete brand identities — end-to-end digital product development.
                </p>
              </div>
              <Link to="/services" className="btn btn--ghost btn--small">
                View All Services
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            </div>
            <div className="grid-3">
              {services.slice(0, 3).map((service) => (
                <ServiceCard key={service.id} service={service} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── Testimonials Preview ── */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-8)' }}>
              Kind Words
            </h2>
            <div className="testimonials-track">
              {testimonials.map((t, i) => (
                <TestimonialCard key={i} {...t} />
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── CTA Section ── */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <CTASection
              title="Have an app idea? Let's build it."
              subtitle="From concept to launch — we handle every aspect of product development."
              primaryLabel="Start a Project"
              secondaryLabel="View Our Work"
              primaryHref="/contact"
              secondaryHref="/work"
            />
          </div>
        </section>
      </ScrollReveal>
    </PageTransition>
  );
}
